<template>
  <section class="space-y-5">
    <!-- Summary -->
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p class="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-400">
          <i class="pi pi-play-circle text-[11px]"></i> Open now
        </p>
        <p class="mt-1 text-2xl font-bold text-primary" data-testid="cbt-count-live">{{ liveTests.length }}</p>
      </div>
      <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <p class="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-400">
          <i class="pi pi-clock text-[11px]"></i> Upcoming
        </p>
        <p class="mt-1 text-2xl font-bold text-gray-800" data-testid="cbt-count-upcoming">{{ upcomingTests.length }}</p>
      </div>
      <div class="col-span-2 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:col-span-1">
        <p class="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-400">
          <i class="pi pi-check-circle text-[11px]"></i> Closed
        </p>
        <p class="mt-1 text-2xl font-bold text-gray-800" data-testid="cbt-count-ended">{{ endedTests.length }}</p>
      </div>
    </div>

    <!-- Search -->
    <IconField iconPosition="left" class="w-full sm:max-w-sm">
      <InputIcon class="pi pi-search" />
      <InputText
        v-model="searchQuery"
        placeholder="Search tests by title..."
        class="w-full"
        data-testid="exam-search-input"
      />
    </IconField>

    <!-- Loading -->
    <div v-if="loading && !tests.length" class="grid gap-4 md:grid-cols-2">
      <div v-for="n in 4" :key="n" class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <Skeleton width="60%" height="1.25rem" />
        <Skeleton width="40%" height="0.75rem" class="mt-2" />
        <Skeleton width="100%" height="4rem" class="mt-4" />
        <Skeleton width="100%" height="2.25rem" class="mt-4" />
      </div>
    </div>

    <template v-else>
      <!-- Available (open now + upcoming) -->
      <div>
        <div class="mb-3 flex items-center gap-2">
          <h2 class="text-base font-semibold text-gray-800">Available Tests</h2>
          <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {{ availableTests.length }}
          </span>
        </div>

        <div v-if="availableTests.length" class="grid gap-4 md:grid-cols-2">
          <CbtTestCard
            v-for="test in availableTests"
            :key="test.id"
            :test="test"
            @start="confirmStart"
          />
        </div>

        <div v-else class="rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center">
          <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
            <i class="pi pi-inbox text-xl text-gray-400"></i>
          </span>
          <p class="mt-3 text-sm font-medium text-gray-600">
            {{ searchQuery ? 'No tests match your search' : 'No tests available right now' }}
          </p>
          <p class="mt-1 text-xs text-gray-400">
            {{ searchQuery ? 'Try a different title.' : 'New tests from your teachers will show up here.' }}
          </p>
        </div>
      </div>

      <!-- Closed -->
      <div v-if="endedTests.length">
        <button
          type="button"
          class="mb-3 flex w-full items-center gap-2 text-left"
          data-testid="btn-toggle-past-exams"
          @click="showEnded = !showEnded"
        >
          <i class="pi text-xs text-gray-400" :class="showEnded ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
          <h2 class="text-base font-semibold text-gray-800">Closed Tests</h2>
          <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {{ endedTests.length }}
          </span>
        </button>

        <div v-if="showEnded" class="grid gap-4 md:grid-cols-2">
          <CbtTestCard v-for="test in endedTests" :key="test.id" :test="test" />
        </div>
      </div>

      <!-- Pagination -->
      <Paginator
        v-if="totalRecords > rows"
        :rows="rows"
        :totalRecords="totalRecords"
        :first="(page - 1) * rows"
        :rowsPerPageOptions="[10, 20, 30]"
        data-testid="pagination-dropdown"
        @page="onPage"
      />
    </template>

    <!-- Start confirmation -->
    <Dialog
      v-model:visible="showDialog"
      modal
      :style="{ width: '30rem', maxWidth: '95vw' }"
      :draggable="false"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <i class="pi pi-play text-primary"></i>
          </span>
          <div class="min-w-0">
            <h3 class="truncate text-base font-bold text-gray-900">{{ selectedTest?.title }}</h3>
            <p class="text-xs text-gray-500">Confirm before you begin</p>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <div v-if="selectedTest?.description" class="rounded-lg bg-gray-50 p-3">
          <p class="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Instructions</p>
          <p class="whitespace-pre-wrap text-sm text-gray-600">{{ selectedTest.description }}</p>
        </div>

        <div class="grid grid-cols-2 gap-3 text-sm">
          <div class="rounded-lg border border-gray-100 p-3">
            <p class="text-xs text-gray-400">Duration</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ selectedTest?.duration }} min</p>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <p class="text-xs text-gray-400">Questions</p>
            <p class="mt-0.5 font-semibold text-gray-800">{{ selectedTest?.questionCount }}</p>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <p class="text-xs text-gray-400">Opens</p>
            <p class="mt-0.5 text-gray-700">{{ selectedTest?.startLabel }}</p>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <p class="text-xs text-gray-400">Closes</p>
            <p class="mt-0.5 text-gray-700">{{ selectedTest?.endLabel }}</p>
          </div>
        </div>

        <div class="flex items-start gap-2 rounded-lg bg-primary/5 p-3 text-xs text-gray-600">
          <i class="pi pi-info-circle mt-0.5 text-primary"></i>
          <span>Once you start, the timer runs continuously — finish and submit before it expires.</span>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          data-testid="btn-cancel-start-exam"
          @click="showDialog = false"
        />
        <Button
          label="Start Now"
          icon="pi pi-play"
          :loading="starting"
          data-testid="btn-start-exam-now"
          @click="startExam"
        />
      </template>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useCBTTests } from '@/composables/queries/cbt/useCBTTest';
import { useCbtTestMutations } from '@/composables/mutations/cbt/useCbtTestMutations';
import CbtTestCard, { type CbtTestCardModel } from './CbtTestCard.vue';

interface RawQuestionable {
  __typename?: string;
  name?: string | null;
  classroom?: { name?: string | null } | null;
  surname?: string | null;
  first_name?: string | null;
  course_code?: string | null;
  course_title?: string | null;
}

interface RawCbtTest {
  id: string;
  title: string;
  description?: string | null;
  duration: number;
  start_time: string;
  end_time?: string | null;
  question_count?: number | null;
  questions?: unknown[] | null;
  questionable?: RawQuestionable | null;
}

const router = useRouter();
const toast = useToast();
const { startCBTTest } = useCbtTestMutations();

const page = ref(1);
const rows = ref(10);
const searchQuery = ref('');
const showDialog = ref(false);
const showEnded = ref(true);
const starting = ref(false);
const selectedTest = ref<CbtTestCardModel | null>(null);

const variables = computed(() => ({ first: rows.value, page: page.value }));
const { cbtTests, refetch } = useCBTTests({ autoFetch: true, variables });

const loading = computed(() => cbtTests.loading.value);
const totalRecords = computed(() => cbtTests.paginatorInfo.value?.total ?? 0);

// Ticks every second so "Open now" / countdowns stay accurate without a reload.
const currentTime = ref(Date.now());
let timerInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  timerInterval = setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onUnmounted(() => clearInterval(timerInterval));

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function describeScope(questionable?: RawQuestionable | null): string {
  if (!questionable) return 'Not assigned';
  if (questionable.__typename === 'ClassroomSection') {
    return `${questionable.classroom?.name ?? ''} ${questionable.name ?? ''}`.trim() || 'Class';
  }
  if (questionable.__typename === 'Student') {
    return `${questionable.surname ?? ''} ${questionable.first_name ?? ''}`.trim() || 'You';
  }
  if (questionable.__typename === 'Course') {
    return `${questionable.course_code ?? ''} — ${questionable.course_title ?? ''}`.trim();
  }
  return 'Not assigned';
}

const tests = computed<CbtTestCardModel[]>(() =>
  (cbtTests.data.value as unknown as RawCbtTest[]).map((raw) => {
    const startsAt = new Date(raw.start_time).getTime();
    const endsAt = raw.end_time ? new Date(raw.end_time).getTime() : null;
    const now = currentTime.value;

    let status: CbtTestCardModel['status'] = 'live';
    if (now < startsAt) status = 'upcoming';
    else if (endsAt && endsAt < now) status = 'ended';

    return {
      id: String(raw.id),
      title: raw.title,
      description: raw.description,
      scope: describeScope(raw.questionable),
      duration: Number(raw.duration) || 0,
      questionCount: raw.question_count ?? raw.questions?.length ?? 0,
      startLabel: dateFormatter.format(new Date(raw.start_time)),
      endLabel: raw.end_time ? dateFormatter.format(new Date(raw.end_time)) : '—',
      status,
      startsInMs: Math.max(0, startsAt - now),
    };
  }),
);

const matchesSearch = (test: CbtTestCardModel) =>
  test.title.toLowerCase().includes(searchQuery.value.trim().toLowerCase());

const liveTests = computed(() => tests.value.filter(t => t.status === 'live' && matchesSearch(t)));
const upcomingTests = computed(() => tests.value.filter(t => t.status === 'upcoming' && matchesSearch(t)));
const endedTests = computed(() => tests.value.filter(t => t.status === 'ended' && matchesSearch(t)));

// Open tests first, then upcoming ordered by whichever starts soonest.
const availableTests = computed(() => [
  ...liveTests.value,
  ...[...upcomingTests.value].sort((a, b) => a.startsInMs - b.startsInMs),
]);

function onPage(event: { page: number; rows: number }) {
  page.value = event.page + 1;
  rows.value = event.rows;
  refetch({ first: rows.value, page: page.value });
}

function confirmStart(test: CbtTestCardModel) {
  selectedTest.value = test;
  showDialog.value = true;
}

async function startExam() {
  const test = selectedTest.value;
  if (!test) return;

  if (test.status !== 'live') {
    toast.add({ severity: 'warn', summary: 'Not Yet Time', detail: 'This test is not open yet.', life: 3000 });
    return;
  }

  starting.value = true;
  try {
    const session = await startCBTTest(test.id);
    if (session?.id) {
      showDialog.value = false;
      router.push({ name: 'cbt-page', params: { id: session.id } });
    }
  } finally {
    starting.value = false;
  }
}
</script>
